import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import * as authService from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // Verificar se usuário está autenticado ao carregar
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = authService.getToken();
      if (!token) {
        setLoading(false);
        return;
      }

      // Verificar se token é válido buscando dados do usuário
      const userData = await authService.getCurrentUser();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      // Token inválido ou expirado - limpar
      authService.clearToken();
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      
      // Armazenar token
      authService.setToken(response.token);
      
      // Atualizar estado
      setUser(response.user);
      setIsAuthenticated(true);
      
      toast.success(`Bem-vindo, ${response.user.name}!`);
      navigate('/dashboard');
      
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erro ao fazer login. Verifique suas credenciais.';
      toast.error(errorMessage);
      throw error;
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      const response = await authService.googleLogin(credential);
      
      // Armazenar token
      authService.setToken(response.token);
      
      // Atualizar estado
      setUser(response.user);
      setIsAuthenticated(true);
      
      toast.success(`Bem-vindo, ${response.user.name}!`);
      navigate('/dashboard');
      
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erro ao fazer login com Google.';
      toast.error(errorMessage);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      
      // Armazenar token
      authService.setToken(response.token);
      
      // Atualizar estado
      setUser(response.user);
      setIsAuthenticated(true);
      
      toast.success(`Conta criada com sucesso! Bem-vindo, ${response.user.name}!`);
      navigate('/dashboard');
      
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erro ao criar conta.';
      toast.error(errorMessage);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Chamar endpoint de logout (opcional, já que é stateless)
      await authService.logout();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      // Limpar estado local
      authService.clearToken();
      setUser(null);
      setIsAuthenticated(false);
      toast.success('Logout realizado com sucesso');
      navigate('/login');
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(prev => ({ ...prev, ...updatedUserData }));
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    loginWithGoogle,
    register,
    logout,
    updateUser,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
